import { useEffect, useState } from 'react';
import { ChevronDown, Edit3, Plus, Trash2 } from 'lucide-react';
import api from '../../lib/api';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Textarea from '../../components/ui/Textarea';
import Modal from '../../components/ui/Modal';

type ContractTemplateRow = {
  id: string;
  name: string;
  bodyMarkdown: string;
  variables: unknown;
  createdAt: string;
};

function previewBody(text: string, maxLen = 140) {
  const oneLine = text.replace(/\s+/g, ' ').trim();
  if (oneLine.length <= maxLen) return oneLine || '—';
  return `${oneLine.slice(0, maxLen)}…`;
}

function variablesToJsonString(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return '';
  }
}

export default function ContractTemplatesPage() {
  const [agencyOrgId, setAgencyOrgId] = useState('');
  const [templates, setTemplates] = useState<ContractTemplateRow[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [modalName, setModalName] = useState('');
  const [modalBody, setModalBody] = useState('');
  const [modalVariablesJson, setModalVariablesJson] = useState('');
  const [workflowExpanded, setWorkflowExpanded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get('/auth/me');
        const agency = me.data.memberships?.find((m: { organization?: { type?: string } }) => m.organization?.type === 'AGENCY');
        if (agency) setAgencyOrgId(agency.organizationId);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const load = async () => {
    if (!agencyOrgId) return;
    const res = await api.get('/contracts/templates', { params: { organizationId: agencyOrgId } });
    setTemplates(res.data);
  };

  useEffect(() => {
    load();
  }, [agencyOrgId]);

  const openNew = () => {
    setEditingId(null);
    setModalName('');
    setModalBody('');
    setModalVariablesJson('');
    setModalOpen(true);
  };

  const openEdit = (t: ContractTemplateRow) => {
    setEditingId(t.id);
    setModalName(t.name);
    setModalBody(t.bodyMarkdown);
    setModalVariablesJson(variablesToJsonString(t.variables));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  const parseVariables = (): unknown | undefined => {
    const raw = modalVariablesJson.trim();
    if (!raw) return undefined;
    try {
      return JSON.parse(raw) as unknown;
    } catch {
      return null;
    }
  };

  const saveTemplate = async () => {
    if (!agencyOrgId || !modalName.trim() || !modalBody.trim()) return;
    const variables = parseVariables();
    if (variables === null) {
      alert('Variables must be valid JSON or left empty.');
      return;
    }
    try {
      if (editingId) {
        await api.put(`/contracts/templates/${editingId}`, {
          name: modalName.trim(),
          bodyMarkdown: modalBody,
          variables: variables ?? null,
        });
      } else {
        await api.post('/contracts/templates', {
          organizationId: agencyOrgId,
          name: modalName.trim(),
          bodyMarkdown: modalBody,
          variables: variables ?? null,
        });
      }
      closeModal();
      load();
    } catch (e) {
      console.error(e);
      alert('Could not save template.');
    }
  };

  const deleteTemplate = async (id: string) => {
    if (!confirm('Delete this contract template?')) return;
    try {
      await api.delete(`/contracts/templates/${id}`);
      load();
    } catch (e) {
      console.error(e);
      alert('Could not delete template.');
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
          <h1 className="text-[1.375rem] font-semibold tracking-tightest text-glass-text md:text-[2.25rem]">Contract templates</h1>
          <p className="mt-1 text-xs text-glass-text-2 md:text-base">
            Reusable contract bodies for campaigns. Use placeholders like <code className="bg-glass-3 border border-glass px-1.5 py-0.5 rounded text-accent2">{'{{jobTitle}}'}</code>,{' '}
            <code className="bg-glass-3 border border-glass px-1.5 py-0.5 rounded text-accent2">{'{{brandName}}'}</code>,{' '}
            <code className="bg-glass-3 border border-glass px-1.5 py-0.5 rounded text-accent2">{'{{today}}'}</code> — plus any keys you define in variables JSON.
          </p>
          </div>
          <Button
            type="button"
            size="icon"
            className="h-11 w-11 shrink-0 md:hidden"
            onClick={openNew}
            disabled={!agencyOrgId}
            aria-label="New contract template"
          >
            <Plus className="h-5 w-5" />
          </Button>
          <Button className="hidden shrink-0 md:inline-flex" onClick={openNew} disabled={!agencyOrgId}>
            New template
          </Button>
        </div>

        <button
          type="button"
          className="mt-3 flex min-h-11 w-full cursor-pointer items-center justify-between rounded-xl border border-glass bg-glass-2 px-3 text-left text-xs font-medium text-glass-text-2 transition-colors hover:bg-glass-3 md:hidden"
          onClick={() => setWorkflowExpanded((expanded) => !expanded)}
          aria-expanded={workflowExpanded}
        >
          How contract workflow works
          <ChevronDown className={`h-4 w-4 transition-transform ${workflowExpanded ? 'rotate-180' : ''}`} />
        </button>
        {workflowExpanded && (
          <p className="mt-2 rounded-xl border border-glass bg-glass-2 p-3 text-xs leading-relaxed text-glass-text-2 md:hidden">
            Workflow: the managing agency (or brand users on the job) generates contracts from the job Contracts tab and can link each draft to a specific KOL invitation. The creator
            sees linked contracts on that invitation, downloads the PDF, and can record acknowledgement after accepting the invite.
          </p>
        )}
        <p className="mt-2 hidden text-sm text-glass-text-2 md:block">
          Workflow: the managing agency (or brand users on the job) generates contracts from the job Contracts tab and can link each draft to a specific KOL invitation. The creator
          sees linked contracts on that invitation, downloads the PDF, and can record acknowledgement after accepting the invite.
        </p>
      </div>

      <Card>
        <CardHeader className="hidden md:flex">
          <CardTitle>Templates</CardTitle>
        </CardHeader>
        <CardContent className="p-0 md:p-6 md:pt-0">
          <ul className="space-y-3">
            {templates.map((t) => (
              <li
                key={t.id}
                className="flex items-start justify-between gap-3 border-b border-glass bg-glass-2 p-[13px] last:border-b-0 md:rounded-xl md:border md:p-4"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-glass-text">{t.name}</div>
                  <p className="mt-1 line-clamp-2 break-words text-sm text-glass-text-2">{previewBody(t.bodyMarkdown)}</p>
                  <p className="text-xs text-glass-text-3 mt-2">
                    Created {new Date(t.createdAt).toLocaleString()}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-9 w-9 p-0 md:w-auto md:px-3"
                    onClick={() => openEdit(t)}
                    aria-label={`Edit ${t.name}`}
                  >
                    <Edit3 className="h-4 w-4" />
                    <span className="ml-1 hidden md:inline">Edit</span>
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-9 w-9 border-red-500/30 p-0 text-red-500 hover:bg-red-500/10 dark:text-red-300 md:w-auto md:px-3"
                    onClick={() => deleteTemplate(t.id)}
                    aria-label={`Delete ${t.name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="ml-1 hidden md:inline">Delete</span>
                  </Button>
                </div>
              </li>
            ))}
          </ul>
          {templates.length === 0 && <p className="px-[13px] py-10 text-center text-sm text-glass-text-2 md:px-0 md:py-0 md:text-left">No contract templates yet.</p>}
        </CardContent>
      </Card>

      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingId ? 'Edit contract template' : 'New contract template'}
        size="xl"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={closeModal}>
              Cancel
            </Button>
            <Button onClick={saveTemplate} disabled={!agencyOrgId || !modalName.trim() || !modalBody.trim()}>
              {editingId ? 'Save changes' : 'Create template'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-glass-text mb-1">Name</label>
            <Input placeholder="e.g. Standard KOL agreement" value={modalName} onChange={(e) => setModalName(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-glass-text mb-1">Body (Markdown)</label>
            <Textarea
              rows={12}
              placeholder={'Agreement between {{brandName}} and …\n\nJob: {{jobTitle}}\nDate: {{today}}'}
              value={modalBody}
              onChange={(e) => setModalBody(e.target.value)}
              className="font-mono text-sm"
            />
            <p className="text-xs text-glass-text-2 mt-1">
              Use double curly braces for placeholders, e.g. <code className="bg-glass-3 border border-glass px-1.5 py-0.5 rounded text-accent2">{'{{kolName}}'}</code>.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-glass-text mb-1">Variables (optional JSON)</label>
            <Textarea
              rows={4}
              placeholder='{ "exampleKey": "description for editors" }'
              value={modalVariablesJson}
              onChange={(e) => setModalVariablesJson(e.target.value)}
              className="font-mono text-sm"
            />
            <p className="text-xs text-glass-text-2 mt-1">Document placeholder metadata for your team; generation still uses runtime values.</p>
          </div>
        </div>
      </Modal>
    </div>
  );
}
